import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, Building2, CreditCard, Wallet } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../components/ui/dialog';
import { useAuthStore } from '../../store';

const Context = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL || '';
export const debtMoney = amount => `${Number(amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`;
export function validateDebtRequirement(data) {
  if (!data || typeof data.enabled !== 'boolean' || typeof data.blocked !== 'boolean' ||
      typeof data.debt_amount !== 'number' || !Number.isFinite(data.debt_amount) || data.debt_amount < 0 ||
      data.blocked !== (data.enabled && data.debt_amount > 0)) throw new Error('Cari borç durumu doğrulanamadı.');
  return data;
}
export function useDebtRequirement() {
  const value = useContext(Context);
  if (!value) throw new Error('DebtRequirementProvider is required');
  return value;
}

export function DebtRequirementProvider({ children }) {
  const token = useAuthStore(s => s.token);
  const user = useAuthStore(s => s.user);
  const viewAs = useAuthStore(s => s.viewAsDealer);
  const viewer = viewAs?.id || user?.id;
  const identity = `${viewer || ''}:${token || ''}`;
  const location = useLocation();
  const generation = useRef(0);
  const [result, setResult] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const refresh = useCallback(async () => {
    const request = ++generation.current;
    setResult(old => ({ identity, loading: true, data: old?.identity === identity ? old.data : null }));
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (viewAs?.id) headers['X-View-As-Dealer'] = viewAs.id;
      const response = await axios.get(`${API}/api/dealer/debt-requirement`, { headers, timeout: 15000 });
      const data = validateDebtRequirement(response.data);
      if (generation.current === request) setResult({ identity, data, loading: false });
      return data;
    } catch (error) {
      if (generation.current === request) setResult({ identity, loading: false, error: 'Cari borç durumu alınamadı. Sipariş vermeden önce tekrar kontrol edin.' });
      throw error;
    }
  }, [identity, token, viewAs?.id]);
  useEffect(() => {
    if (token) refresh().catch(() => {});
    return () => { generation.current += 1; };
  }, [refresh, token, location.pathname]);
  useEffect(() => { setDismissed(false); }, [identity]);
  useEffect(() => { if (location.pathname === '/dealer') setDismissed(false); }, [location.pathname]);
  const loading = result?.identity !== identity || result?.loading;
  const data = result?.identity === identity ? result.data : null;
  const error = result?.identity === identity ? result.error : null;
  const ready = !loading && !error && Boolean(data);
  const paymentRoute = /\/(payment|payments|debt-payment)(\/|$)/.test(location.pathname) || location.pathname.includes('/payment-cari');
  return <Context.Provider value={{ data, loading, error, ready, refresh, blocked: Boolean(data?.blocked), debtAmount: data?.debt_amount || 0 }}>
    {children}
    <Dialog open={ready && data.blocked && !dismissed && !paymentRoute} onOpenChange={open => { if (!open) setDismissed(true); }}>
      <DialogContent className="max-w-lg rounded-2xl p-0 overflow-hidden" data-testid="mandatory-debt-dialog">
        <div className="p-6 sm:p-8 bg-gradient-to-br from-red-50 to-white">
          <span className="inline-flex p-3 rounded-2xl bg-red-100 text-red-700 mb-4"><Wallet size={26}/></span>
          <DialogTitle className="text-2xl text-gray-900">Vadesi Geçmiş Borç</DialogTitle>
          <DialogDescription className="mt-2 text-gray-600">Sipariş verebilmek için mevcut cari borcunuzu ödemeniz gerekiyor. Sepetinizdeki ürünler korunur.</DialogDescription>
          <div className="my-6 p-5 rounded-xl bg-white border border-red-100"><p className="text-xs text-gray-500 mb-1">Ödenmesi gereken cari borç</p><strong className="text-3xl sm:text-4xl font-bold text-red-700 tabular-nums" data-testid="mandatory-debt-amount">{debtMoney(data?.debt_amount)}</strong></div>
          <DebtPaymentActions/>
          <p className="text-xs text-gray-500 leading-relaxed mt-4">Kredi kartı ödemeniz doğrulandığında veya havale/EFT tahsilatınız cari hesabınıza işlendiğinde sipariş verebilirsiniz.</p>
        </div>
      </DialogContent>
    </Dialog>
  </Context.Provider>;
}

export function DebtPaymentActions() {
  const navigate = useNavigate();
  return <div className="grid gap-2 sm:grid-cols-2">
    <button type="button" onClick={() => navigate('/dealer/debt-payment?method=credit_card')} className="flex justify-center items-center gap-2 rounded-xl bg-red-700 hover:bg-red-800 text-white p-3 text-sm font-semibold" data-testid="debt-pay-card"><CreditCard size={17}/>Kartla Hemen Öde</button>
    <button type="button" onClick={() => navigate('/dealer/debt-payment?method=bank_transfer')} className="flex justify-center items-center gap-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50 p-3 text-sm font-semibold text-gray-700" data-testid="debt-pay-transfer"><Building2 size={17}/>EFT / Havale ile Öde</button>
  </div>;
}

export function DebtRequirementBanner() {
  const { ready, blocked, debtAmount, error, refresh } = useDebtRequirement();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  if (error) return <div role="alert" className="mb-4 p-3 rounded-xl border border-amber-200 bg-amber-50 text-sm text-amber-900 flex flex-wrap items-center justify-between gap-2"><span>{error}</span><button onClick={() => refresh().catch(() => {})} className="font-semibold underline">Tekrar Kontrol Et</button></div>;
  if (!ready || !blocked || pathname === '/dealer/debt-payment') return null;
  return <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 flex flex-wrap items-center justify-between gap-3" data-testid="mandatory-debt-banner"><div className="flex gap-2 text-red-900 text-sm"><AlertCircle className="shrink-0" size={19}/><p>Sipariş İçin Vadesi Geçmiş Borcunuzu Kapatınız: <strong>{debtMoney(debtAmount)}</strong></p></div><button onClick={() => navigate('/dealer/debt-payment')} className="inline-flex items-center gap-1 text-sm font-semibold text-red-700">Hemen Öde <ArrowRight size={15}/></button></div>;
}
